/**
 * ResponsesNewShell Component
 *
 * Two-column inline layout: response sidebar (left) + detail view (right).
 * Similar architecture to ComparisonDetail view.
 * - Collapsible sidebar with response cards
 * - Inline detail view (not modal) when response selected
 * - Site header/nav/footer remain visible
 *
 * Uses response favorite gateway placeholder for future persistence.
 * Includes structured telemetry with correlationId tracking.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { logger } from '../../../lib/logging';
import { logEvent, createTimer, logError, getCorrelationId } from '../../../utils/telemetry';
import type { ResponsesIndexProps } from './ResponsesIndex';
import { ResponseCard } from './ResponseCard';
import { ResponseDetailInline } from './ResponseDetailInline';
import { ResponseDetails } from './ResponseDetails';
import { formatModelDisplayName } from '../../../utils/modelDisplayName';
import { createNoopResponseFavoriteGateway } from '../../../lib/favorites/responseFavoritesGateway';
import { getModelLogo } from '../../../utils/logoMapping';
import { usePageControls } from '../../../contexts/PageControlsContext';
import { useDevice } from '../../../features/consensus/hooks/useDevice';
import { BackControlsDual } from '../shared/BackControlsDual';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

type ResponseEntry = ResponsesIndexProps['responses'][number];

const normalizeLabel = (value?: string | null): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const resolveModelDisplayName = (response: ResponseEntry): string =>
  normalizeLabel(response.model?.displayName) ??
  normalizeLabel(response.model?.name) ??
  normalizeLabel(response.modelName) ??
  formatModelDisplayName(response.modelId);

const resolveProviderName = (response: ResponseEntry): string | undefined =>
  normalizeLabel(response.provider?.name) ??
  normalizeLabel(response.providerName);

// S9: Import score types and components
import { ScoreChip } from '../ScoreChip';
import type { CompositeScore } from '../../../hooks/useComparisonScores';

const buildCriterionScoreMap = (criterionScores?: CompositeScore['criterionScores']): Record<string, number> | null => {
  if (!criterionScores || !Array.isArray(criterionScores)) {
    return null;
  }

  const mapped: Record<string, number> = {};

  criterionScores.forEach(criterion => {
    const name = typeof criterion?.criterionName === 'string' ? criterion.criterionName : null;
    const value = typeof criterion?.score === 'number' ? criterion.score : null;

    if (!name || value === null || Number.isNaN(value)) {
      return;
    }

    mapped[name] = value;
  });

  return Object.keys(mapped).length > 0 ? mapped : null;
};

export interface ResponsesNewShellProps extends ResponsesIndexProps {
  initialResponseId?: string | null;
  onNavigateToAll?: () => void;
  compositeScores?: CompositeScore[]; // S9
  scoresEnabled?: boolean; // S9
  scoringResults?: Array<{ responseId: string; score: number }>; // For navigation order consistency
  onDetailOpenChange?: (isOpen: boolean) => void;
  onDetailVisit?: () => void;
  isAnonymous?: boolean;
}

export const ResponsesNewShell: React.FC<ResponsesNewShellProps> = ({
  responses,
  comparisons,
  density = 'comfortable',
  searchQuery,
  onChatOpen,
  onOpenParent,
  onNavigateToAll,
  initialResponseId,
  compositeScores,
  scoresEnabled = false,
  scoringResults = [],
  onDetailOpenChange,
  onDetailVisit,
  isAnonymous = false,
}) => {
  const { isMobile } = useDevice();
  const pageControls = usePageControls();

  // Local state for favorites (optimistic UI)
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const favoriteGateway = useMemo(() => createNoopResponseFavoriteGateway(), []);
  const compositeScoresByModel = useMemo(() => {
    if (!compositeScores || compositeScores.length === 0) {
      return null;
    }

    const map = new Map<string, CompositeScore>();
    compositeScores.forEach(score => {
      if (score?.modelId) {
        map.set(score.modelId, score);
      }
    });

    return map;
  }, [compositeScores]);
  const resolveResponseScores = useCallback((response: ResponseEntry): Record<string, number> | undefined => {
    if (response?.scores && Object.keys(response.scores).length > 0) {
      return response.scores as Record<string, number>;
    }

    if (!compositeScoresByModel) {
      return undefined;
    }

    const compositeScore = compositeScoresByModel.get(response.modelId);
    const fallbackScores = compositeScore ? buildCriterionScoreMap(compositeScore.criterionScores) : null;

    return fallbackScores ?? undefined;
  }, [compositeScoresByModel]);

  const resolveResponseOverallScore = useCallback(
    (response: ResponseEntry): number | undefined => {
      if (typeof response?.scoreOverall === 'number' && Number.isFinite(response.scoreOverall)) {
        return response.scoreOverall;
      }

      if (!compositeScoresByModel) {
        return undefined;
      }

      const compositeScore = compositeScoresByModel.get(response.modelId);
      if (compositeScore && typeof compositeScore.compositeScore === 'number' && Number.isFinite(compositeScore.compositeScore)) {
        return compositeScore.compositeScore;
      }

      return undefined;
    },
    [compositeScoresByModel]
  );

  // Full-screen detail state (Step 3)
  const [detailOpen, setDetailOpen] = useState(!!initialResponseId);
  const [selectedResponseId, setSelectedResponseId] = useState<string | null>(initialResponseId || null);

  // Animation state for logo transition (lifted from ResponseDetails for desktop chevrons)
  const [showLogoTransition, setShowLogoTransition] = useState(false);
  const [transitionLogo, setTransitionLogo] = useState<string | null>(null);

  // Update state when initialResponseId changes (e.g., when navigating from Comparison detail)
  useEffect(() => {
    if (initialResponseId) {
      setSelectedResponseId(initialResponseId);
      setDetailOpen(true);
      onDetailVisit?.();
    }
  }, [initialResponseId, onDetailVisit]);

  useEffect(() => {
    onDetailOpenChange?.(detailOpen);
  }, [detailOpen, onDetailOpenChange]);

  // Navigation logic: Get adjacent responses in same comparison
  const getAdjacentResponses = useCallback(
    (currentResponseId: string) => {
      const current = responses.find((r) => r.id === currentResponseId);
      if (!current) {
        return { prev: null, next: null };
      }

      // Filter responses in same comparison
      const comparisonResponsesList = responses.filter((r) => r.comparisonId === current.comparisonId);

      // Compute avgScore for each response using the same logic as ComparisonDetail
      const responsesWithScores = comparisonResponsesList.map(response => {
        let avgScore = 0;

        // Priority 1: Use compositeScores from LLM judge hook
        if (scoresEnabled && compositeScores && compositeScores.length > 0) {
          const compositeScore = compositeScores.find(cs => cs.modelId === response.modelId);
          if (compositeScore) {
            avgScore = compositeScore.compositeScore;
          }
        }
        // Priority 2: Use legacy scoringResults
        else if (scoringResults.length > 0) {
          const scores = scoringResults
            .filter(s => s.responseId === response.id)
            .map(s => s.score);

          if (scores.length > 0) {
            avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
          } else if (response.scoreOverall != null) {
            // Priority 3: Fallback to response-level scoreOverall
            avgScore = response.scoreOverall;
          }
        }
        // Priority 3: Fallback to response-level scoreOverall
        else if (response.scoreOverall != null) {
          avgScore = response.scoreOverall;
        }

        return { response, avgScore };
      });

      // Sort by avgScore descending (matches ComparisonDetail sorting)
      const sortedResponses = responsesWithScores.sort((a, b) => b.avgScore - a.avgScore);

      const currentIndex = sortedResponses.findIndex((r) => r.response.id === currentResponseId);

      return {
        prev: currentIndex > 0 ? sortedResponses[currentIndex - 1].response : null,
        next: currentIndex < sortedResponses.length - 1 ? sortedResponses[currentIndex + 1].response : null,
      };
    },
    [responses, compositeScores, scoresEnabled, scoringResults]
  );

  // Handle navigation to previous response
  const handleNavigateToPrev = useCallback(() => {
    if (!selectedResponseId) return;

    const timer = createTimer();
    const correlationId = getCorrelationId();
    const { prev } = getAdjacentResponses(selectedResponseId);

    if (prev) {
      setSelectedResponseId(prev.id);
      logEvent({
        op: 'navigate_prev',
        component: 'FullscreenResponseView',
        success: true,
        fromResponseId: selectedResponseId,
        toResponseId: prev.id,
        comparisonId: prev.comparisonId,
        correlationId,
        duration_ms: timer.end(),
      });
    }
  }, [selectedResponseId, getAdjacentResponses]);

  // Handle navigation to next response
  const handleNavigateToNext = useCallback(() => {
    if (!selectedResponseId) return;

    const timer = createTimer();
    const correlationId = getCorrelationId();
    const { next } = getAdjacentResponses(selectedResponseId);

    if (next) {
      setSelectedResponseId(next.id);
      logEvent({
        op: 'navigate_next',
        component: 'FullscreenResponseView',
        success: true,
        fromResponseId: selectedResponseId,
        toResponseId: next.id,
        comparisonId: next.comparisonId,
        correlationId,
        duration_ms: timer.end(),
      });
    }
  }, [selectedResponseId, getAdjacentResponses]);

  // Animation-aware navigation handlers for desktop chevrons
  const handleNavigateToPrevWithAnimation = useCallback(() => {
    if (!selectedResponseId) return;

    const { prev } = getAdjacentResponses(selectedResponseId);

    if (prev) {
      // Get the logo for the previous response
      const prevLogo = getModelLogo(prev.modelId, prev.providerId);

      // Show logo transition animation
      setTransitionLogo(prevLogo);
      setShowLogoTransition(true);

      // Wait for animation (500ms) before navigating
      setTimeout(() => {
        handleNavigateToPrev();
        setShowLogoTransition(false);
        setTransitionLogo(null);
      }, 500);
    }
  }, [selectedResponseId, getAdjacentResponses, handleNavigateToPrev]);

  const handleNavigateToNextWithAnimation = useCallback(() => {
    if (!selectedResponseId) return;

    const { next } = getAdjacentResponses(selectedResponseId);

    if (next) {
      // Get the logo for the next response
      const nextLogo = getModelLogo(next.modelId, next.providerId);

      // Show logo transition animation
      setTransitionLogo(nextLogo);
      setShowLogoTransition(true);

      // Wait for animation (500ms) before navigating
      setTimeout(() => {
        handleNavigateToNext();
        setShowLogoTransition(false);
        setTransitionLogo(null);
      }, 500);
    }
  }, [selectedResponseId, getAdjacentResponses, handleNavigateToNext]);

  // Log mount event with telemetry (Step 10)
  useEffect(() => {
    const timer = createTimer();
    const correlationId = getCorrelationId();

    logEvent({
      op: 'responses_shell_mount',
      component: 'ResponsesNewShell',
      correlationId,
      success: true,
      responsesCount: responses.length,
      comparisonsCount: comparisons.length,
      density,
      hasSearchQuery: Boolean(searchQuery),
      duration_ms: timer.end(),
    });

    // Legacy logger for backwards compatibility
    logger.info('responses_new_shell_mount', undefined, {
      op: 'mount',
      component: 'ResponsesNewShell',
      correlationId,
      success: true,
    });
  }, []); // Only log on mount

  // Handle favorite toggle with optimistic UI (Step 10: Enhanced telemetry)
  const handleFavoriteToggle = useCallback(async (responseId: string) => {
    const timer = createTimer();
    const correlationId = getCorrelationId();
    const wasFavorited = favorites.has(responseId);
    const newFavorites = new Set(favorites);

    // Optimistic update
    if (wasFavorited) {
      newFavorites.delete(responseId);
    } else {
      newFavorites.add(responseId);
    }
    setFavorites(newFavorites);

    try {
      await favoriteGateway.toggle(responseId, !wasFavorited);

      logEvent({
        op: 'favorite_intent',
        component: 'ResponsesNewShell',
        success: true,
        correlationId,
        responseId,
        isFavorite: !wasFavorited,
        persisted: false,
        duration_ms: timer.end(),
      });
    } catch (error) {
      // Revert optimistic update on error
      setFavorites(favorites);

      logError('favorite_intent', 'ResponsesNewShell', error, {
        responseId,
        correlationId,
        persisted: false,
        duration_ms: timer.end(),
      });
    }
  }, [favoriteGateway, favorites]);

  // Handle detail view open (Step 3)
  const handleOpenDetail = (response: typeof responses[0]) => {
    const timer = createTimer();

    setSelectedResponseId(response.id);
    setDetailOpen(true);
    onDetailVisit?.();

    logEvent({
      op: 'open_from_card',
      component: 'ResponseCard',
      success: true,
      target_id: response.id,
      comparisonId: response.comparisonId,
      duration_ms: timer.end(),
    });

    // Call original handler for backward compatibility
    onChatOpen?.(response);
  };

  // Handle detail view close (Step 3)
  const handleCloseDetail = () => {
    const timer = createTimer();

    logEvent({
      op: 'back_to_list',
      component: 'FullscreenResponseView',
      success: true,
      responseId: selectedResponseId,
      duration_ms: timer.end(),
    });

    setDetailOpen(false);
    setSelectedResponseId(null);
  };

  // Get selected response object
  const selectedResponse = responses.find(r => r.id === selectedResponseId);

  const handleCopySelectedResponse = useCallback(async () => {
    if (!selectedResponse) {
      return;
    }

    const timer = createTimer();
    const content = selectedResponse.response || selectedResponse.content || '';

    try {
      await navigator.clipboard.writeText(content);
      logEvent({
        op: 'copy_response',
        component: 'ResponseDetails',
        success: true,
        responseId: selectedResponse.id,
        contentLength: content.length,
        duration_ms: timer.end(),
      });
    } catch (error) {
      logError('copy_response', 'ResponseDetails', error, {
        responseId: selectedResponse.id,
        duration_ms: timer.end(),
      });
    }
  }, [selectedResponse]);

  const handleShareSelectedResponse = useCallback(async () => {
    if (!selectedResponse) {
      return;
    }

    const timer = createTimer();
    const modelShareName = resolveModelDisplayName(selectedResponse);
    const shareData = {
      title: `Response from ${modelShareName}`,
      text: (selectedResponse.response || selectedResponse.content || '').substring(0, 500),
      url: window.location.href,
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        logEvent({
          op: 'share_response',
          component: 'ResponseDetails',
          success: true,
          responseId: selectedResponse.id,
          method: 'web_share_api',
          duration_ms: timer.end(),
        });
      } else {
        const shareUrl = `${window.location.origin}/results?responseId=${selectedResponse.id}`;
        await navigator.clipboard.writeText(shareUrl);
        logEvent({
          op: 'share_response',
          component: 'ResponseDetails',
          success: true,
          responseId: selectedResponse.id,
          method: 'clipboard_fallback',
          duration_ms: timer.end(),
        });
      }
    } catch (error) {
      logError('share_response', 'ResponseDetails', error, {
        responseId: selectedResponse.id,
        duration_ms: timer.end(),
      });
    }
  }, [selectedResponse]);

  const handleFavoriteSelectedResponse = useCallback(() => {
    if (!selectedResponse) {
      return;
    }

    handleFavoriteToggle(selectedResponse.id);
  }, [selectedResponse, handleFavoriteToggle]);

  const headerActions = !isAnonymous && selectedResponse
    ? (
        <>
          <button
            onClick={handleCopySelectedResponse}
            className="p-2 bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50 rounded-lg text-slate-300 hover:text-white transition-colors"
            title="Copy response"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </button>
        </>
      )
    : null;

  // Find parent comparison for each response
  const getParentComparison = (comparisonId: string) => {
    return comparisons.find((c) => c.id === comparisonId);
  };

  // Navigate to parent comparison from response detail view
  const handleNavigateToParentFromResponse = useCallback(() => {
    if (!selectedResponse) return;

    const comparisonId = selectedResponse.comparisonId;
    logger.info('ResponseDetail: Navigating to parent comparison', {
      responseId: selectedResponse.id,
      comparisonId
    });

    // Close detail view and navigate to parent comparison
    setDetailOpen(false);
    setSelectedResponseId(null);
    onOpenParent(comparisonId);
  }, [selectedResponse, onOpenParent]);

  // Move navigation controls into the main header for mobile response detail
  useEffect(() => {
    if (!isMobile) {
      pageControls.setNavSlot(null);
      return;
    }

    if (!detailOpen || !selectedResponse) {
      pageControls.setNavSlot(null);
      return;
    }

    const parentComparison = getParentComparison(selectedResponse.comparisonId);

    pageControls.setNavSlot(
      <BackControlsDual
        parentTitle={parentComparison?.title}
        onNavigateToParent={handleNavigateToParentFromResponse}
        onNavigateToAll={onNavigateToAll}
        showParentButton={true}
        useAllButton={true}
      />
    );

    return () => pageControls.setNavSlot(null);
  }, [
    detailOpen,
    getParentComparison,
    handleNavigateToParentFromResponse,
    isMobile,
    onNavigateToAll,
    pageControls,
    selectedResponse,
  ]);

  // Set isResponseDetail flag for mobile response detail view
  useEffect(() => {
    if (!isMobile) {
      return () => pageControls.setIsResponseDetail(false);
    }

    if (detailOpen && selectedResponse) {
      pageControls.setIsResponseDetail(true);
    } else {
      pageControls.setIsResponseDetail(false);
    }

    return () => {
      pageControls.setIsResponseDetail(false);
    };
  }, [detailOpen, selectedResponse, isMobile, pageControls]);

  // Determine winners (highest scoreOverall per comparison)
  const winners = useMemo(() => {
    const winnerSet = new Set<string>();

    if (responses.length === 0) {
      return winnerSet;
    }

    // Group responses by comparison
    const byComparison = new Map<string, typeof responses>();
    responses.forEach((r) => {
      if (!byComparison.has(r.comparisonId)) {
        byComparison.set(r.comparisonId, []);
      }
      byComparison.get(r.comparisonId)!.push(r);
    });

    // Find winner for each comparison using composite/inline scores
    byComparison.forEach((compResponses) => {
      if (compResponses.length === 0) {
        return;
      }

      const winner = compResponses.reduce((best, current) => {
        const bestScore = resolveResponseOverallScore(best);
        const currentScore = resolveResponseOverallScore(current);

        const normalizedBest = typeof bestScore === 'number' && Number.isFinite(bestScore) ? bestScore : -1;
        const normalizedCurrent =
          typeof currentScore === 'number' && Number.isFinite(currentScore) ? currentScore : -1;

        return normalizedCurrent > normalizedBest ? current : best;
      }, compResponses[0]);

      const winningScore = resolveResponseOverallScore(winner);
      if (typeof winningScore === 'number' && Number.isFinite(winningScore)) {
        winnerSet.add(winner.id);
      }
    });

    return winnerSet;
  }, [responses, resolveResponseOverallScore]);

  // Grid layout classes based on density
  const gridClasses = {
    comfortable: 'grid grid-cols-1 md:grid-cols-2 gap-4',
    compact: 'grid grid-cols-1 md:grid-cols-3 gap-3',
    stacked: 'grid grid-cols-1 gap-4',
  };

  if (responses.length === 0) {
    return (
      <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 text-center">
        <h2 className="text-xl font-semibold text-white mb-2">No responses yet</h2>
        <p className="text-slate-400 text-sm">
          Responses will appear here once models finish generating output.
        </p>
      </div>
    );
  }

  // Show detail view OR response list (not both)
  if (detailOpen && selectedResponse) {
    // Construct navigation content for desktop Action Bar
    const comparisonResponsesCount = responses.filter((r) => r.comparisonId === selectedResponse.comparisonId).length;
    const { prev, next } = getAdjacentResponses(selectedResponse.id);
    const navigationContent = !isMobile ? (
      <div className="flex items-center gap-4">
        {comparisonResponsesCount > 1 && (
          <button
            onClick={handleNavigateToPrevWithAnimation}
            disabled={!prev}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous response"
          >
            <ChevronLeftIcon className="w-7 h-7" />
          </button>
        )}

        {/* Model Identity */}
        <div className="flex items-center gap-3">
          <img
            src={getModelLogo(selectedResponse.modelId, selectedResponse.providerId)}
            alt={resolveModelDisplayName(selectedResponse)}
            className="w-8 h-8 rounded object-contain"
          />
          <span className="text-slate-200 font-medium text-base">
            {resolveModelDisplayName(selectedResponse)}
          </span>
        </div>

        {comparisonResponsesCount > 1 && (
          <button
            onClick={handleNavigateToNextWithAnimation}
            disabled={!next}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Next response"
          >
            <ChevronRightIcon className="w-7 h-7" />
          </button>
        )}
      </div>
    ) : null;

    return (
      <div className="w-full">
        <ResponseDetailInline
          onClose={handleCloseDetail}
          onNavigateToParent={handleNavigateToParentFromResponse}
          onNavigateToAll={onNavigateToAll}
          parentTitle={getParentComparison(selectedResponse.comparisonId)?.title || 'Comparison'}
          headerActions={headerActions}
          navigationContent={navigationContent}
        >
          <ResponseDetails
            modelId={selectedResponse.modelId}
            providerId={selectedResponse.providerId}
            modelDisplayName={resolveModelDisplayName(selectedResponse)}
            providerName={resolveProviderName(selectedResponse)}
            score={resolveResponseOverallScore(selectedResponse)}
            scores={resolveResponseScores(selectedResponse)}
            isWinner={winners.has(selectedResponse.id)}
            isFavorite={favorites.has(selectedResponse.id)}
            content={selectedResponse.response || selectedResponse.content || ''}
            prompt={getParentComparison(selectedResponse.comparisonId)?.title}
            metadata={{
              tokens: selectedResponse.tokenUsage?.totalTokens || selectedResponse.tokenCount,
              latency: selectedResponse.latencyMs || selectedResponse.responseTime,
              cost: selectedResponse.cost,
            }}
            comparisonTitle={getParentComparison(selectedResponse.comparisonId)?.title}
            comparisonId={selectedResponse.comparisonId}
            createdAt={selectedResponse.createdAt}
            responseTime={selectedResponse.responseTime}
            status={selectedResponse.status}
            onOpenComparison={onOpenParent}
            onCopy={handleCopySelectedResponse}
            onShare={handleShareSelectedResponse}
            onFavoriteToggle={handleFavoriteSelectedResponse}
            onNavigatePrev={handleNavigateToPrev}
            onNavigateNext={handleNavigateToNext}
            prevModelLogo={
              selectedResponseId
                ? getAdjacentResponses(selectedResponseId).prev
                  ? getModelLogo(getAdjacentResponses(selectedResponseId).prev!.modelId, getAdjacentResponses(selectedResponseId).prev!.providerId)
                  : undefined
                : undefined
            }
            nextModelLogo={
              selectedResponseId
                ? getAdjacentResponses(selectedResponseId).next
                  ? getModelLogo(getAdjacentResponses(selectedResponseId).next!.modelId, getAdjacentResponses(selectedResponseId).next!.providerId)
                  : undefined
                : undefined
            }
            canNavigatePrev={selectedResponseId ? !!getAdjacentResponses(selectedResponseId).prev : false}
            canNavigateNext={selectedResponseId ? !!getAdjacentResponses(selectedResponseId).next : false}
            showLogoTransition={isMobile ? undefined : showLogoTransition}
            transitionLogo={isMobile ? undefined : transitionLogo}
          />
        </ResponseDetailInline>
      </div>
    );
  }

  // Response list view (when no detail open)
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Responses</h2>
        <p className="text-slate-400 text-sm mt-1">
          {responses.length} {responses.length === 1 ? 'response' : 'responses'}
          {searchQuery && ` matching "${searchQuery}"`}
        </p>
      </div>

      {/* Response Cards Grid */}
      <div className={gridClasses[density]} role="list">
        {responses.map((response) => {
          const parentComparison = getParentComparison(response.comparisonId);
          const isWinner = winners.has(response.id);
          const isFavorite = favorites.has(response.id);

          // S9: Find composite score for this response
          const responseScore = scoresEnabled && compositeScoresByModel
            ? compositeScoresByModel.get(response.modelId)
            : undefined;

          return (
            <div key={response.id} className="relative">
              {responseScore && (
                <div className="absolute top-2 right-2 z-10">
                  <ScoreChip score={responseScore.compositeScore} size="sm" showLabel={false} />
                </div>
              )}
              <ResponseCard
                key={response.id}
                response={response}
                parentComparison={parentComparison}
                isWinner={isWinner}
                isFavorite={isFavorite}
                onClick={() => {
                  logger.info('response_card_clicked', undefined, {
                    op: 'response_card_clicked',
                    component: 'ResponsesNewShell',
                    responseId: response.id,
                    comparisonId: response.comparisonId,
                    isWinner,
                  });
                  handleOpenDetail(response);
                }}
                onOpenParent={onOpenParent}
                onFavoriteToggle={handleFavoriteToggle}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
